import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Step,
  StepButton,
  Stepper,
} from "@mui/material";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { AutonomyMeasurementsStep } from "./steps/AutonomyMeasurementsStep";
import { BasicInfoStep } from "./steps/BasicInfoStep";
import { buildPermission, getCustomerId } from "common/utils/auth";
import { capitalize } from "common/utils/strings";
import { ChooseArchetypeStep } from "./steps/ChooseArchetypeStep";
import { CustomerSelector } from "portal/components/customers/CustomerSelector";
import { DefaultHandlers, typedPath, TypedPathWrapper } from "typed-path";
import {
  editingSupported,
  implementToViewModel,
  ImplementViewModel,
  viewModelSchema,
  viewModelToImplement,
} from "./ImplementViewModel";
import { Form, Formik } from "formik";
import { getImplementsPath } from "portal/utils/routing";
import { Loading } from "portal/components/Loading";
import { NameAndPhotoStep } from "./steps/NameAndPhotoStep";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { PtoRequirementsStep } from "./steps/PtoRequirementsStep";
import { ReviewStep } from "./steps/ReviewStep";
import { ScvStep } from "./steps/ScvStep";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { validateThen } from "portal/utils/forms";
import LockIcon from "@mui/icons-material/LockOutlined";
import React, { FC, useState } from "react";

interface ImplementFormProps {
  implementId?: string;
}

const S = typedPath<ImplementViewModel>();

const stepFields: Record<number, TypedPathWrapper<unknown, DefaultHandlers>[]> =
  {
    0: [S.archetype],
    1: [S.name],
    2: [S.hitchType, S.atim],
    3: [
      S.workingWidthM,
      S.workingStartOffsetM,
      S.workingEndOffsetM,
      S.boundaryViewModel,
      S.toolSpeedMS,
      S.transitionSpeedMS,
      S.runUpDistanceM,
      S.straightenDistanceM,
      S.turningRadiusLimits,
      S.overrideAutonomyMeasurements,
    ],
    4: [S.ptoRequirements],
    5: [S.scvInputs],
    // 6: Review — no editable fields
  };

export const ImplementForm: FC<ImplementFormProps> = ({ implementId }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState<number>(0);
  const navigate = useNavigate();
  const steps: string[] = [
    t("views.autotractor.equipment.chooseTemplate"),
    t("views.autotractor.equipment.nameNoPhoto"),
    t("views.autotractor.equipment.basicInfo"),
    t("views.autotractor.equipment.autonomyMeasurements"),
    t("models.equipment.pto_short"),
    t("models.equipment.scv_short", { count: 1 }),
    t("views.autotractor.equipment.review"),
  ];
  const { user } = useSelf();
  const userCustomer = getCustomerId(user);
  const [customerId, setCustomerId] = useState<number | undefined>(
    userCustomer
  );
  const isEditing = Boolean(implementId);

  const {
    data: implementsRes,
    isError,
    isLoading,
  } = useQueryPopups(
    atkEquipmentApi.endpoints.searchImplements.useQuery(
      { implementId },
      { skip: !isEditing }
    )
  );
  if (isError) {
    navigate(getImplementsPath());
  }
  const existingImplement = implementsRes?.implements.at(0);

  const [createImplement] = useMutationPopups(
    atkEquipmentApi.endpoints.createImplement.useMutation(),
    {
      success: capitalize(
        t("utils.actions.createdLong", {
          subject: t("models.equipment.implement_one"),
        })
      ),
    }
  );

  const [updateImplement, { isLoading: isSaving }] = useMutationPopups(
    atkEquipmentApi.endpoints.updateImplement.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: t("models.equipment.implement_one"),
        })
      ),
    }
  );

  const customerSelectorAuthorized = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );

  if (isEditing && isLoading) {
    return <Loading />;
  }

  return (
    <Formik
      initialValues={implementToViewModel(existingImplement)}
      validationSchema={viewModelSchema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        const newImplement = viewModelToImplement(values);

        if (implementId) {
          await updateImplement(newImplement);
        } else {
          const res = await createImplement({
            ...newImplement,
            customerId: customerId?.toString(),
          });
          if (!res.error) {
            navigate(getImplementsPath());
          }
        }
        setSubmitting(false);
      }}
    >
      {(formikProps) => {
        const disableNextButton =
          ((activeStep === 0 && !formikProps.values.archetype) ||
            activeStep === steps.length - 1) &&
          !isEditing;
        const editingUnsupportedVersion = !editingSupported(formikProps.values);
        const buttonRow = (
          <div className="flex justify-between">
            {activeStep > 0 && (
              <Button
                color="inherit"
                onClick={() => setActiveStep(activeStep - 1)}
              >
                {t("utils.actions.backShort")}
              </Button>
            )}
            <div className="flex ml-auto gap-2">
              {activeStep !== steps.length - 1 && (
                <Button
                  className="ml-auto"
                  color={isEditing ? "inherit" : "primary"}
                  variant={isEditing ? "text" : "contained"}
                  disabled={disableNextButton}
                  onClick={() =>
                    validateThen(
                      formikProps,
                      () => setActiveStep(activeStep + 1),
                      undefined,
                      stepFields[activeStep]
                    )
                  }
                >
                  {t("utils.actions.next")}
                </Button>
              )}
              {(isEditing || activeStep === steps.length - 1) && (
                <Button
                  className="ml-auto"
                  type="submit"
                  variant="contained"
                  endIcon={
                    isSaving && (
                      <CircularProgress size="small" variant="indeterminate" />
                    )
                  }
                  disabled={
                    !formikProps.isValid ||
                    !formikProps.dirty ||
                    formikProps.isValidating ||
                    formikProps.isSubmitting
                  }
                >
                  {isEditing
                    ? t("utils.actions.save")
                    : t("utils.actions.submit")}
                </Button>
              )}
            </div>
          </div>
        );
        return (
          <Form className="flex flex-col gap-2">
            {customerSelectorAuthorized && !isEditing && (
              <CustomerSelector
                value={customerId}
                onChange={(c) => setCustomerId(c)}
              />
            )}
            <Stepper nonLinear={isEditing} activeStep={activeStep}>
              {steps.map((s, i) => (
                <Step key={`${i}-${s}`} index={i}>
                  <StepButton
                    onClick={() =>
                      i > activeStep
                        ? validateThen(
                            formikProps,
                            () => setActiveStep(i),
                            undefined,
                            stepFields[activeStep]
                          )
                        : setActiveStep(i)
                    }
                  >
                    {s}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
            {buttonRow}
            {editingUnsupportedVersion && (
              <Alert icon={<LockIcon fontSize="inherit" />} severity="warning">
                <AlertTitle>
                  {t(
                    "views.autotractor.equipment.unsupportedVersion.implementLockedTitle"
                  )}
                </AlertTitle>
                {t(
                  "views.autotractor.equipment.unsupportedVersion.implementLockedBody"
                )}
              </Alert>
            )}
            {activeStep === 0 && (
              <ChooseArchetypeStep disabled={editingUnsupportedVersion} />
            )}
            {activeStep === 1 && (
              <NameAndPhotoStep disabled={editingUnsupportedVersion} />
            )}
            {activeStep === 2 && (
              <BasicInfoStep disabled={editingUnsupportedVersion} />
            )}
            {activeStep === 3 && (
              <AutonomyMeasurementsStep disabled={editingUnsupportedVersion} />
            )}
            {activeStep === 4 && (
              <PtoRequirementsStep disabled={editingUnsupportedVersion} />
            )}
            {activeStep === 5 && (
              <ScvStep disabled={editingUnsupportedVersion} />
            )}
            {activeStep === 6 && <ReviewStep />}
            {buttonRow}
          </Form>
        );
      }}
    </Formik>
  );
};
